import React, { useState, useEffect, useMemo, useRef } from "react";

import { useModal } from "../contexts/ModalContext";
import { DEFAULT_THEME } from "../constants/theme";
import { matchSearch } from "../utils/chosung";
import {
    DEFAULT_DEPT_COLORS,
    LOCAL_ICONS,
    getLogIcon,
    getSentimentIcon,
    getTypeColor,
    getTypeIcon,
} from "./counseling/counselingUiUtils";
import useCounselingGestures from "./counseling/useCounselingGestures";
import useCounselingDerivedData from "./counseling/useCounselingDerivedData";
import VisitLogModal from "../components/VisitLogModal";
import CounselingSharingModal from "./counseling/CounselingSharingModal";
import CounselingLogCreationModal from "./counseling/CounselingLogCreationModal";
import CounselingStatsSection from "./counseling/CounselingStatsSection";
import CounselingDetailModal from "./counseling/CounselingDetailModal";
import CounselingMobileListBoard from "./counseling/CounselingMobileListBoard";
import CounselingSidebarPanel from "./counseling/CounselingSidebarPanel";
import CounselingTimelineHeader from "./counseling/CounselingTimelineHeader";
import CounselingTimelineContent from "./counseling/CounselingTimelineContent";
import CounselingRecentLogsContent from "./counseling/CounselingRecentLogsContent"; // [NEW] 최근 심방 타임라인
import CounselingMobileTimelineOverlay from "./counseling/CounselingMobileTimelineOverlay";

export default function CounselingView({
    students = [],
    teachers = [],
    visitLogs = [],
    onAddLog,
    onDeleteLog,
    onUpdateLog,
    initialStudentId,
    initialLogId,
    // onOpenDetail, // Removed prop
    onClearTargetLogId,
    onNavigateToAttendance, // Optional if passed, otherwise ignored

    onUpdateStudent, // [NEW] 학생 정보 업데이트 (기도제목 연동용)
    attendanceLogs = [], // [NEW] 출석 기록 (for Detail Modal)

    // [NEW] Lifted State Props
    searchQuery = "",
    setSearchQuery,
    targetGroup = "students",
    setTargetGroup,
    selectedDepartment,

    // [NEW] Permission Props
    user,
    userProfile,

    theme = DEFAULT_THEME
}) {
    // --- 상태 관리 ---
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // [NEW] Permission check - teachers can only edit/delete their own logs
    const isTeacher = userProfile?.memberRole === 'teacher';
    const currentUserId = user?.uid;

    const [selectedStudentId, setSelectedStudentId] = useState(initialStudentId || null);

    // [NEW] 대상 그룹 관리 (Props)
    const targetList = targetGroup === 'students' ? students : teachers;

    // [REMOVED] Local  & targetGroup state
    const [filterType, setFilterType] = useState("all"); // all, recent, long_absent
    const [sortType, setSortType] = useState("name"); // name, recent, count
    const [viewingLog, setViewingLog] = useState(null); // 상세/수정 모달용

    const [viewingStudent, setViewingStudent] = useState(null); // [NEW] 학생 상세 모달
    const [sharingStudent, setSharingStudent] = useState(null); // [NEW] 공유 모달
    const [viewMode, setViewMode] = useState('list'); // 'timeline' or 'list' [NEW] Default to list for easier viewing
    const [isCreatingLog, setIsCreatingLog] = useState(false); // [NEW] 작성 모달 상태


    // 작성 폼 상태 (Quick Input)
    const [inputContent, setInputContent] = useState("");
    const [inputType, setInputType] = useState("면담"); // 면담, 전화, 문자, 기타
    const [inputDate, setInputDate] = useState(new Date().toISOString().slice(0, 10));

    const [inputSentiment, setInputSentiment] = useState("neutral"); // happy, neutral, sad

    // [NEW] 모달 내부 수정 상태
    const [isModalEditing, setIsModalEditing] = useState(false);
    const [modalEditContent, setModalEditContent] = useState("");
    const [modalEditDate, setModalEditDate] = useState("");
    const [modalEditType, setModalEditType] = useState("");
    const [modalEditSentiment, setModalEditSentiment] = useState("neutral");

    useEffect(() => {
        const handleEsc = (event) => {
            if (event.key === "Escape") {
                if (isCreatingLog) setIsCreatingLog(false);
                if (viewingLog) setViewingLog(null);
                if (viewingStudent) setViewingStudent(null);
                if (sharingStudent) setSharingStudent(null);
            }
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [isCreatingLog, viewingLog, viewingStudent, sharingStudent]);

    const { openConfirm, openAlert } = useModal();

    const timelineRef = useRef(null);
    const {
        onTouchStart,
        onTouchMove,
        onListSwipeEnd,
        onModalSwipeEnd,
        onVerticalSwipeEnd,
        creationModalPos,
        handleMouseDown,
        logDetailModalPos,
        handleLogDetailMouseDown,
        detailModalPos,
        handleDetailMouseDown,
    } = useCounselingGestures({
        targetGroup,
        setTargetGroup,
        isMobile,
        isCreatingLog,
        viewingLog,
        viewingStudent,
    });

    // --- 데이터 가공 ---
    const {
        filteredStudents,
        selectedStudent,
        studentLogs,
        thisMonthLogs,
        longAbsentStudents,
        studentMetrics,
    } = useCounselingDerivedData({
        targetList,
        searchQuery,
        filterType,
        selectedDepartment,
        targetGroup,
        sortType,
        visitLogs,
        selectedStudentId,
        students,
        matchSearch,
    });

    // [NEW] 필터나 검색으로 인해 선택된 학생이 목록에서 사라지면 선택 해제 (데스크톱 전용 자연스러운 흐름)
    useEffect(() => {
        if (!isMobile && selectedStudentId && filteredStudents.length > 0) {
            const isStillInList = filteredStudents.some(s => s.id === selectedStudentId);
            if (!isStillInList) {
                // 선택 해제하지 않고 유지하고 싶을 수도 있지만, 보통 필터링된 결과 내에서 노는 것이 자연스러움
                // 하지만 유저가 명시적으로 검색 중일 때는 유지하는게 나을 수도 있음. 
                // 여기서는 '필터 버튼' 클릭 시에만 반응하도록 filterType을 의존성에 넣는 것도 방법.
            }
        }
    }, [filteredStudents, isMobile, selectedStudentId]);

    // [NEW] 필터 타입 변경 시 정렬 방식 자동 최적화
    const handleSetFilterType = (type) => {
        setFilterType(type);
        if (type === 'recent') {
            setSortType('recent');
        } else if (type === 'all') {
            setSortType('name');
        }
    };


    // --- 초기화 효과 ---
    useEffect(() => {
        if (initialStudentId) setSelectedStudentId(initialStudentId);
    }, [initialStudentId]);

    useEffect(() => {
        if (initialLogId && visitLogs.length > 0) {
            const target = visitLogs.find(l => l.id === initialLogId);
            if (target) {
                setSelectedStudentId(target.studentId);
                if (onClearTargetLogId) onClearTargetLogId();
            }
        }
    }, [initialLogId, visitLogs, onClearTargetLogId]);

    // [NEW] viewingLog 변경 시 모달 수정 상태 초기화
    useEffect(() => {
        if (viewingLog) {
            setIsModalEditing(false);
            setModalEditContent(viewingLog.content || "");
            setModalEditDate(viewingLog.date || "");
            setModalEditType(viewingLog.type || "");
            setModalEditSentiment(viewingLog.sentiment || "neutral");
        }
    }, [viewingLog]);


    const [editingLogId, setEditingLogId] = useState(null);

    // --- 핸들러 ---
    // [NEW] 부서 색상 클래스 가져오기 함수
    const getDeptColorClass = (deptName) => {
        if (userProfile?.departmentColors?.[deptName]) {
            return userProfile.departmentColors[deptName];
        }
        return DEFAULT_DEPT_COLORS[deptName] || "bg-[#354F38]";
    };

    const handleStudentClick = (id) => {
        if (selectedStudentId === id) {
            setSelectedStudentId(null);
        } else {
            setSelectedStudentId(id);
            setInputContent(""); // 입력창 초기화
            setEditingLogId(null);

            // [NEW] 모바일에서는 클릭 시 상세 모달을 즉시 엽니다.
            if (isMobile) {
                const student = targetList.find(s => s.id === id);
                if (student) setViewingStudent(student);
            }
        }
    };

    const handleQuickSubmit = (e) => {
        e.preventDefault();
        if (!selectedStudentId) {
            openAlert("학생을 먼저 선택해주세요.", "알림");
            return;
        }
        if (!inputContent.trim()) return;

        // [NEW] '기도' 타입이면 학생의 기도제목도 자동 업데이트
        if (inputType === '기도' && onUpdateStudent) {
            onUpdateStudent({
                ...selectedStudent,
                prayerTopic: inputContent
            });
        }

        if (editingLogId) {
            // 수정 모드
            const updatedLog = {
                id: editingLogId,
                studentId: selectedStudentId,
                studentName: selectedStudent.name,
                date: inputDate,
                type: inputType,
                content: inputContent,
                sentiment: inputSentiment,
                // createdAt은 유지하거나 수정일 추가
            };
            onUpdateLog(updatedLog);
            setEditingLogId(null);
            setIsCreatingLog(false); // Close modal after edit
            // setShowInput(false); // Potential legacy code, keeping commented out or removing if not needed.
        } else {
            // 신규 작성
            const newLog = {
                studentId: selectedStudentId,
                studentName: selectedStudent.name,
                date: inputDate,
                type: inputType,
                content: inputContent,
                sentiment: inputSentiment,
                createdAt: new Date().toISOString(),
                createdBy: currentUserId, // [NEW] Track who created this log
                createdByName: userProfile?.name || '알 수 없음' // [NEW] 작성자 이름 저장
            };
            onAddLog(newLog);

            setIsCreatingLog(false); // 작성 완료 후 닫기
        }

        setInputContent("");
        // 날짜나 타입은 유지할지 초기화할지 결정 (일단 유지)
    };

    const handleEditStart = (log) => {
        setEditingLogId(log.id);
        setInputDate(log.date);
        setInputType(log.type);
        setInputSentiment(log.sentiment);
        setInputContent(log.content);
        setInputContent(log.content);
        setIsCreatingLog(true); // 수정 시 입력창 열기
        // 입력창으로 스크롤 부드럽게 이동? (선택 사항)
    };

    const handleCancelEdit = () => {
        setEditingLogId(null);
        setInputContent("");
    };

    const handleDeleteLog = (id) => {
        openConfirm("기록 삭제", "정말 이 기록을 삭제하시겠습니까?", () => {
            onDeleteLog(id);
            setViewingLog(null); // [Moved Here] Close after confirm
            if (editingLogId === id) handleCancelEdit();
        });
    };

    // [NEW] Permission helper - check if user can edit/delete a log
    // Admin: can edit all logs / Teacher: can only edit their own logs
    const canEditLog = (log) => {
        if (!isTeacher) return true; // Admin can edit all
        return log.createdBy === currentUserId; // Teacher can only edit own
    };


    // [NEW] 모달 내부 저장 핸들러
    const handleSaveModalEdit = () => {
        if (!viewingLog) return;
        const updatedLog = {
            ...viewingLog,
            content: modalEditContent,
            date: modalEditDate,
            type: modalEditType,
            sentiment: modalEditSentiment
        };
        onUpdateLog(updatedLog);

        // 즉시 반영을 위해 viewingLog 상태 업데이트
        setViewingLog(updatedLog);
        setIsModalEditing(false);
    };

    // --- 렌더링 ---
    return (
        <div className="w-full h-full flex flex-col min-h-0">
            {/* 전체 스크롤 복구 (다른 게시판 규격) */}
            <div className={`flex-1 overflow-y-auto custom-scrollbar ${isMobile ? '' : 'pt-6 px-4 pb-24'}`}>
                <div className={`w-full mx-auto ${isMobile ? '' : 'max-w-7xl space-y-6'}`}>

                    {/* === 모바일 전용 UI === */}
                    {isMobile && (
                        <CounselingMobileListBoard
                            onTouchStart={onTouchStart}
                            onTouchMove={onTouchMove}
                            onTouchEnd={onListSwipeEnd}
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            sortType={sortType}
                            setSortType={setSortType}
                            filteredStudents={filteredStudents}
                            theme={theme}
                            visitLogs={visitLogs}
                            thisMonthLogs={thisMonthLogs}
                            longAbsentStudents={longAbsentStudents}
                            selectedStudentId={selectedStudentId}
                            handleStudentClick={handleStudentClick}
                            getDeptColorClass={getDeptColorClass}
                            filterType={filterType}
                            setFilterType={handleSetFilterType}
                            studentMetrics={studentMetrics}
                        />
                    )}

                    {/* === 데스크톱 전용 UI (Header & KPI) === */}
                    {!isMobile && (
                        <div className="flex-shrink-0 space-y-6 mb-6">
                            {/* [0] 타이틀 헤더 */}
                            <div className="flex flex-col md:flex-row justify-between items-end gap-6">
                                <div>
                                    <h2 className="text-4xl font-black text-gray-900 flex items-center gap-3 font-do-hyeon tracking-tight">
                                        <img src={LOCAL_ICONS.heart} width={56} alt="icon" className="drop-shadow-xl hover:rotate-12 transition-transform duration-500" /> 심방일지
                                    </h2>
                                    <p className="text-gray-400 text-sm font-bold mt-2 ml-1 flex items-center gap-2">
                                        한 영혼 한 영혼, 예수님의 마음으로 기록합니다.
                                    </p>
                                </div>
                            </div>

                            {/* KPI Cards */}
                            <CounselingStatsSection
                                filteredStudents={filteredStudents}
                                visitLogs={visitLogs}
                                isMobile={isMobile}
                                theme={theme}
                                LOCAL_ICONS={LOCAL_ICONS}
                            />
                        </div>
                    )}

                    {/* 메인 그리드 (데스크톱 - 내부 스크롤 영역 활성화를 위해 높이 지정) */}
                    {!isMobile && (
                        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6 h-[850px]">

                            {/* [좌측] 학생 리스트 사이드바 (Glass List) */}
                            <CounselingSidebarPanel
                                isMobile={isMobile}
                                isTeacher={isTeacher}
                                setTargetGroup={setTargetGroup}
                                targetGroup={targetGroup}
                                theme={theme}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                filterType={filterType}
                                setFilterType={handleSetFilterType}
                                filteredStudents={filteredStudents}
                                selectedStudentId={selectedStudentId}
                                visitLogs={visitLogs}
                                handleStudentClick={handleStudentClick}
                                sortType={sortType}
                                setSortType={setSortType}
                                studentMetrics={studentMetrics}
                            />

                            {/* [우측] Soul Care Timeline (3D Canvas) */}
                            <div className="bg-[#F5F7F6]/80 backdrop-blur-xl rounded-[3rem] shadow-inner flex flex-col h-full relative border border-white/50 z-0 overflow-hidden">
                                {!selectedStudent ? (
                                    <CounselingRecentLogsContent
                                        visitLogs={visitLogs}
                                        students={students}
                                        teachers={teachers}
                                        onStudentClick={handleStudentClick}
                                        onLogClick={setViewingLog}
                                        theme={theme}
                                    />
                                ) : (
                                    <>
                                        <CounselingTimelineHeader
                                            selectedStudent={selectedStudent}
                                            isMobile={isMobile}
                                            theme={theme}
                                            targetGroup={targetGroup}
                                            studentLogs={studentLogs}
                                            setViewingStudent={setViewingStudent}
                                            setViewMode={setViewMode}
                                            viewMode={viewMode}
                                            setIsCreatingLog={setIsCreatingLog}
                                            isCreatingLog={isCreatingLog}
                                        />

                                        <CounselingTimelineContent
                                            studentLogs={studentLogs}
                                            isMobile={isMobile}
                                            viewMode={viewMode}
                                            getTypeColor={getTypeColor}
                                            getTypeIcon={getTypeIcon}
                                            getLogIcon={getLogIcon}
                                            setViewingLog={setViewingLog}
                                            canEditLog={canEditLog}
                                            handleEditStart={handleEditStart}
                                            handleDeleteLog={handleDeleteLog}
                                            getSentimentIcon={getSentimentIcon}
                                            editingLogId={editingLogId}
                                            timelineRef={timelineRef}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Mobile Timeline Overlay (Detail View) */}
                    {isMobile && (
                        <CounselingMobileTimelineOverlay
                            selectedStudent={selectedStudent}
                            studentLogs={studentLogs}
                            onTouchStart={onTouchStart}
                            onTouchMove={onTouchMove}
                            onModalSwipeEnd={onModalSwipeEnd}
                            setSelectedStudentId={setSelectedStudentId}
                            getTypeColor={getTypeColor}
                            getTypeIcon={getTypeIcon}
                            canEditLog={canEditLog}
                            handleEditStart={handleEditStart}
                            handleDeleteLog={handleDeleteLog}
                            theme={theme}
                            setIsCreatingLog={setIsCreatingLog}
                        />
                    )}

                    {/* Modals */}
                    <CounselingDetailModal
                        viewingStudent={viewingStudent}
                        isMobile={isMobile}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onModalSwipeEnd={onModalSwipeEnd}
                        setViewingStudent={setViewingStudent}
                        setIsCreatingLog={setIsCreatingLog}
                        visitLogs={visitLogs}
                        attendanceLogs={attendanceLogs}
                        setViewingLog={setViewingLog}
                        userProfile={userProfile}
                        detailModalPos={detailModalPos}
                        handleDetailMouseDown={handleDetailMouseDown}
                        theme={theme}
                        setSharingStudent={setSharingStudent}
                        openAlert={openAlert}
                    />
                    <CounselingSharingModal
                        sharingStudent={sharingStudent}
                        onClose={() => setSharingStudent(null)}
                        theme={theme}
                        openAlert={openAlert}
                    />
                    <VisitLogModal
                        log={viewingLog}
                        onClose={() => setViewingLog(null)}
                        onUpdate={onUpdateLog}
                        onDelete={onDeleteLog}
                        userProfile={userProfile}
                        theme={theme}
                    />
                    <CounselingLogCreationModal
                        isCreatingLog={isCreatingLog}
                        isMobile={isMobile}
                        getLogIcon={getLogIcon}
                        inputType={inputType}
                        getTypeColor={getTypeColor}
                        creationModalPos={creationModalPos}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onVerticalSwipeEnd={onVerticalSwipeEnd}
                        setIsCreatingLog={setIsCreatingLog}
                        handleMouseDown={handleMouseDown}
                        setInputType={setInputType}
                        theme={theme}
                        inputDate={inputDate}
                        setInputDate={setInputDate}
                        inputSentiment={inputSentiment}
                        setInputSentiment={setInputSentiment}
                        getSentimentIcon={getSentimentIcon}
                        inputContent={inputContent}
                        setInputContent={setInputContent}
                        handleQuickSubmit={handleQuickSubmit}
                        editingLogId={editingLogId}
                    />
                </div>
            </div>
        </div>
    );
}
